import React, { useState, useEffect, useRef } from 'react';

function RewardsTab({ config, onUpdate, onAddVoucher, onUpdateVoucher, onDeleteVoucher, onAddTokenRule, onUpdateTokenRule, onDeleteTokenRule, voucherSchemas, tokenList }) {
  const [formData, setFormData] = useState({
    types: ['VOUCHER'],
    tokenRules: [],
    voucherPool: [],
    voucherRotationEnabled: true
  });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [isAddingVoucher, setIsAddingVoucher] = useState(false);
  const [isAddingToken, setIsAddingToken] = useState(false);
  const [editingVoucherId, setEditingVoucherId] = useState(null);
  const [editingTokenId, setEditingTokenId] = useState(null);
  const [newVoucher, setNewVoucher] = useState({ name: '', schemaId: '', inventory: '' });
  const [newToken, setNewToken] = useState({ tokenId: '', tokenType: '', spendAmount: '', earnAmount: '' });
  const [editingVoucher, setEditingVoucher] = useState({ name: '', schemaId: '', inventory: '' });
  const [editingToken, setEditingToken] = useState({ tokenId: '', tokenType: '', spendAmount: '', earnAmount: '' });
  
  // Track if user has manually selected a reward type
  const userSelectedTypeRef = useRef(false);

  useEffect(() => {
    if (config?.rewards) {
      // Preserve the current reward type selection when config updates
      // This prevents switching from TOKEN back to VOUCHER after adding/updating token rules
      setFormData(prev => {
        const newData = { ...config.rewards };
        // If user has manually selected a type during this session, preserve it
        if (userSelectedTypeRef.current && prev.types && prev.types.length > 0) {
          newData.types = prev.types;
        } else {
          // Use the saved types from config (this is what user saved)
          // Only fall back to inference if types is not set
          if (config.rewards.types && config.rewards.types.length > 0) {
            newData.types = config.rewards.types;
          } else if (config.rewards.tokenRules && config.rewards.tokenRules.length > 0) {
            newData.types = ['TOKEN'];
          } else if (config.rewards.voucherPool && config.rewards.voucherPool.length > 0) {
            newData.types = ['VOUCHER'];
          } else {
            newData.types = ['VOUCHER'];
          }
        }
        return newData;
      });
    }
  }, [config]);

  // 单选逻辑 - 只能选择一种奖励类型
  const handleTypeToggle = (type) => {
    userSelectedTypeRef.current = true;
    setFormData(prev => ({ ...prev, types: [type] }));
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      await onUpdate({ 
        rewards: {
          ...formData,
          voucherRotationEnabled: formData.voucherRotationEnabled
        }
      });
      setMessage({ type: 'success', text: 'Reward settings saved successfully!' });
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to save changes. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const handleAddVoucher = async () => {
    if (!newVoucher.name.trim() || !newVoucher.schemaId.trim()) return;
    // If inventory is provided, it must be > 0
    if (newVoucher.inventory !== '' && (parseInt(newVoucher.inventory, 10) <= 0)) return;
    await onAddVoucher(newVoucher);
    setNewVoucher({ name: '', schemaId: '', inventory: '' });
    setIsAddingVoucher(false);
    setEditingVoucherId(null);
  };

  const handleAddTokenRule = async () => {
    const spendNum = parseFloat(newToken.spendAmount);
    const earnNum = parseFloat(newToken.earnAmount);
    if (!newToken.tokenType.trim() || !spendNum || spendNum <= 0 || !earnNum || earnNum <= 0) return;
    await onAddTokenRule(newToken);
    setNewToken({ tokenId: '', tokenType: '', spendAmount: '', earnAmount: '' });
    setIsAddingToken(false);
    setEditingTokenId(null);
  };

  const handleStartEditVoucher = (voucher) => {
    setEditingVoucherId(voucher.id);
    setEditingVoucher({
      name: voucher.name,
      schemaId: voucher.schemaId,
      inventory: voucher.inventory || ''
    });
    setIsAddingVoucher(false);
    setEditingTokenId(null);
  };

  const handleCancelEditVoucher = () => {
    setEditingVoucherId(null);
    setEditingVoucher({ name: '', schemaId: '', inventory: '' });
  };

  const handleSaveEditVoucher = async () => {
    if (!editingVoucher.name.trim() || !editingVoucher.schemaId.trim()) return;
    // If inventory is provided, it must be > 0
    if (editingVoucher.inventory !== '' && (parseInt(editingVoucher.inventory, 10) <= 0)) return;
    await onUpdateVoucher(editingVoucherId, editingVoucher);
    setEditingVoucherId(null);
    setEditingVoucher({ name: '', schemaId: '', inventory: '' });
  };

  const handleStartEditToken = (rule) => {
    setEditingTokenId(rule.id);
    setEditingToken({
      tokenId: rule.tokenId || '',
      tokenType: rule.tokenType,
      spendAmount: rule.spendAmount,
      earnAmount: rule.earnAmount
    });
    setIsAddingToken(false);
    setEditingVoucherId(null);
  };

  const handleCancelEditToken = () => {
    setEditingTokenId(null);
    setEditingToken({ tokenId: '', tokenType: '', spendAmount: '', earnAmount: '' });
  };

  const handleSaveEditToken = async () => {
    const spendNum = parseFloat(editingToken.spendAmount);
    const earnNum = parseFloat(editingToken.earnAmount);
    if (!editingToken.tokenType.trim() || !spendNum || spendNum <= 0 || !earnNum || earnNum <= 0) return;
    await onUpdateTokenRule(editingTokenId, editingToken);
    setEditingTokenId(null);
    setEditingToken({ tokenId: '', tokenType: '', spendAmount: '', earnAmount: '' });
  };

  return (
    <div className="tab-panel">
      <div className="tab-header">
        <h2>Reward Rules</h2>
        <p>Configure reward types and issuance logic for approved claims.</p>
      </div>

      <div className="rules-form">
        {/* Reward Types */}
        <div className="form-section">
          <div className="form-section-title">Reward Type (Select One)</div>
          <p style={{ color: '#64748b', fontSize: '0.875rem', margin: '0 0 0.75rem 0' }}>
            Choose either Voucher or Token as the reward type. Only one can be active at a time.
          </p>
          <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
              <input
                type="radio"
                name="rewardType"
                checked={formData.types.includes('VOUCHER')}
                onChange={() => handleTypeToggle('VOUCHER')}
                style={{ width: '18px', height: '18px' }}
              />
              <span style={{ fontWeight: '500' }}>Vouchers</span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
              <input
                type="radio"
                name="rewardType"
                checked={formData.types.includes('TOKEN')}
                onChange={() => handleTypeToggle('TOKEN')}
                style={{ width: '18px', height: '18px' }}
              />
              <span style={{ fontWeight: '500' }}>Tokens</span>
            </label>
          </div>
        </div>

        {/* Voucher Pool */}
        {formData.types.includes('VOUCHER') && (
          <div className="form-section">
            <div className="form-section-title">Voucher Pool</div>
            <p style={{ color: '#64748b', fontSize: '0.875rem', margin: '0 0 1rem 0' }}>
              Vouchers are issued in rotation order. After the last voucher, it cycles back to the first.
            </p>

            <div className="items-list">
              {(formData.voucherPool || []).length === 0 ? (
                <div className="empty-state-mini">
                  <p>No vouchers in pool. Add vouchers to enable rewards.</p>
                </div>
              ) : (
                (formData.voucherPool || []).map((voucher, index) => (
                  editingVoucherId === voucher.id ? (
                    <div key={voucher.id} className="item-card" style={{ background: '#f8fafc', flexDirection: 'column', gap: '0.75rem', alignItems: 'stretch' }}>
                      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                        {voucherSchemas && voucherSchemas.length > 0 ? (
                          <select
                            value={editingVoucher.schemaId}
                            onChange={(e) => {
                              const selected = voucherSchemas.find(s => s.id === e.target.value);
                              setEditingVoucher({ 
                                ...editingVoucher, 
                                schemaId: e.target.value,
                                name: selected?.name || editingVoucher.name
                              });
                            }}
                            style={{ flex: 2, minWidth: '200px', padding: '0.5rem', border: '1.5px solid #e2e8f0', borderRadius: '6px' }}
                            autoFocus
                          >
                            <option value="">Select voucher from VIO...</option>
                            {voucherSchemas.map(schema => (
                              <option key={schema.id} value={schema.id}>
                                {schema.name} {schema.isLimited ? `(${schema.quantity ?? 'Limited'} available)` : '(Unlimited)'}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <>
                            <input
                              type="text"
                              placeholder="Voucher name"
                              value={editingVoucher.name}
                              onChange={(e) => setEditingVoucher({ ...editingVoucher, name: e.target.value })}
                              style={{ flex: 1, minWidth: '150px', padding: '0.5rem', border: '1.5px solid #e2e8f0', borderRadius: '6px' }}
                              autoFocus
                            />
                            <input
                              type="text"
                              placeholder="Campaign Voucher Schema ID"
                              value={editingVoucher.schemaId}
                              onChange={(e) => setEditingVoucher({ ...editingVoucher, schemaId: e.target.value })}
                              style={{ flex: 1, minWidth: '150px', padding: '0.5rem', border: '1.5px solid #e2e8f0', borderRadius: '6px' }}
                            />
                          </>
                        )}
                        {(() => {
                          const selectedSchema = voucherSchemas?.find(s => s.id === editingVoucher.schemaId);
                          const maxQuantity = selectedSchema?.isLimited ? selectedSchema.quantity : undefined;
                          return (
                            <input
                              type="number"
                              min="1"
                              max={maxQuantity}
                              placeholder={maxQuantity ? `Max ${maxQuantity}` : "Pool limit (optional)"}
                              value={editingVoucher.inventory}
                              onChange={(e) => {
                                const val = e.target.value;
                                if (maxQuantity && parseInt(val, 10) > maxQuantity) {
                                  setEditingVoucher({ ...editingVoucher, inventory: maxQuantity.toString() });
                                } else {
                                  setEditingVoucher({ ...editingVoucher, inventory: val });
                                }
                              }}
                              style={{ width: '140px', padding: '0.5rem', border: '1.5px solid #e2e8f0', borderRadius: '6px' }}
                              title={maxQuantity ? `Max ${maxQuantity} available in VIO` : "Max number to issue from this pool (leave empty for unlimited)"}
                            />
                          );
                        })()}
                      </div>
                      {!voucherSchemas?.length && (
                        <div style={{ fontSize: '0.75rem', color: '#94a3b8', fontStyle: 'italic' }}>
                          ⚠️ Could not load VIO vouchers. Enter schema ID manually or check authentication.
                        </div>
                      )}
                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
                        <button className="btn-action secondary" onClick={handleCancelEditVoucher}>
                          Cancel
                        </button>
                        <button 
                          className="btn-action primary" 
                          onClick={handleSaveEditVoucher}
                          disabled={!editingVoucher.schemaId || !editingVoucher.name}
                        >
                          Save Changes
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div key={voucher.id} className="item-card">
                      <div className="item-info">
                        <div className="item-icon" style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' }}>
                          #{voucher.order || index + 1}
                        </div>
                        <div className="item-details">
                          <h4>{voucher.name}</h4>
                          <span>
                            Schema: {voucher.schemaId.substring(0, 12)}... 
                            {voucher.inventory && ` • Inventory: ${voucher.issued || 0}/${voucher.inventory}`}
                          </span>
                        </div>
                      </div>
                      <div className="item-actions">
                        <button 
                          className="btn-icon-sm"
                          onClick={() => handleStartEditVoucher(voucher)}
                          title="Edit"
                          style={{ marginRight: '0.5rem' }}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                          </svg>
                        </button>
                        <button 
                          className="btn-icon-sm delete"
                          onClick={() => onDeleteVoucher(voucher.id)}
                          title="Remove"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M3 6h18"></path>
                            <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
                            <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
                          </svg>
                        </button>
                      </div>
                    </div>
                  )
                ))
              )}

              {isAddingVoucher && !editingVoucherId ? (
                <div className="item-card" style={{ background: '#f8fafc', flexDirection: 'column', gap: '0.75rem', alignItems: 'stretch' }}>
                  <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                    {voucherSchemas && voucherSchemas.length > 0 ? (
                      <select
                        value={newVoucher.schemaId}
                        onChange={(e) => {
                          const selected = voucherSchemas.find(s => s.id === e.target.value);
                          setNewVoucher({ 
                            ...newVoucher, 
                            schemaId: e.target.value,
                            name: selected?.name || ''
                          });
                        }}
                        style={{ flex: 2, minWidth: '200px', padding: '0.5rem', border: '1.5px solid #e2e8f0', borderRadius: '6px' }}
                        autoFocus
                      >
                        <option value="">Select voucher from VIO...</option>
                        {voucherSchemas.map(schema => (
                          <option key={schema.id} value={schema.id}>
                            {schema.name} {schema.isLimited ? `(${schema.quantity ?? 'Limited'} available)` : '(Unlimited)'}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <>
                        <input
                          type="text"
                          placeholder="Voucher name"
                          value={newVoucher.name}
                          onChange={(e) => setNewVoucher({ ...newVoucher, name: e.target.value })}
                          style={{ flex: 1, minWidth: '150px', padding: '0.5rem', border: '1.5px solid #e2e8f0', borderRadius: '6px' }}
                          autoFocus
                        />
                        <input
                          type="text"
                          placeholder="Campaign Voucher Schema ID"
                          value={newVoucher.schemaId}
                          onChange={(e) => setNewVoucher({ ...newVoucher, schemaId: e.target.value })}
                          style={{ flex: 1, minWidth: '150px', padding: '0.5rem', border: '1.5px solid #e2e8f0', borderRadius: '6px' }}
                        />
                      </>
                    )}
                    {(() => {
                      const selectedSchema = voucherSchemas?.find(s => s.id === newVoucher.schemaId);
                      const maxQuantity = selectedSchema?.isLimited ? selectedSchema.quantity : undefined;
                      return (
                        <input
                          type="number"
                          min="1"
                          max={maxQuantity}
                          placeholder={maxQuantity ? `Max ${maxQuantity}` : "Pool limit (optional)"}
                          value={newVoucher.inventory}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (maxQuantity && parseInt(val, 10) > maxQuantity) {
                              setNewVoucher({ ...newVoucher, inventory: maxQuantity.toString() });
                            } else {
                              setNewVoucher({ ...newVoucher, inventory: val });
                            }
                          }}
                          style={{ width: '140px', padding: '0.5rem', border: '1.5px solid #e2e8f0', borderRadius: '6px' }}
                          title={maxQuantity ? `Max ${maxQuantity} available in VIO` : "Max number to issue from this pool (leave empty for unlimited)"}
                        />
                      );
                    })()}
                  </div>
                  {!voucherSchemas?.length && (
                    <div style={{ fontSize: '0.75rem', color: '#94a3b8', fontStyle: 'italic' }}>
                      ⚠️ Could not load VIO vouchers. Enter schema ID manually or check authentication.
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
                    <button className="btn-action secondary" onClick={() => setIsAddingVoucher(false)}>
                      Cancel
                    </button>
                    <button 
                      className="btn-action primary" 
                      onClick={handleAddVoucher}
                      disabled={!newVoucher.schemaId || !newVoucher.name}
                    >
                      Add Voucher
                    </button>
                  </div>
                </div>
              ) : !editingVoucherId ? (
                <button className="add-item-btn" onClick={() => {
                  setIsAddingVoucher(true);
                  setEditingVoucherId(null);
                }}>
                  <span>+</span> Add Voucher to Pool
                </button>
              ) : null}
            </div>
          </div>
        )}

        {/* Token Rules */}
        {formData.types.includes('TOKEN') && (
          <div className="form-section">
            <div className="form-section-title">Token Conversion Rule</div>
            <p style={{ color: '#64748b', fontSize: '0.875rem', margin: '0 0 1rem 0' }}>
              Select one token type and define the spend-to-token conversion ratio. Only one token type can be configured.
            </p>
            
            <div className="items-list">
              {(formData.tokenRules || []).length === 0 ? (
                <div className="empty-state-mini">
                  <p>No token configured. Add a token rule to enable token rewards.</p>
                </div>
              ) : (
                (formData.tokenRules || []).map(rule => (
                  editingTokenId === rule.id ? (
                    <div key={rule.id} className="item-card" style={{ background: '#f8fafc', flexDirection: 'column', gap: '0.75rem', alignItems: 'stretch' }}>
                      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
                        {tokenList && tokenList.length > 0 ? (
                          <select
                            value={editingToken.tokenId}
                            onChange={(e) => {
                              const selected = tokenList.find(t => t.id === e.target.value);
                              setEditingToken({ 
                                ...editingToken, 
                                tokenId: e.target.value,
                                tokenType: selected ? `${selected.name} (${selected.symbol})` : editingToken.tokenType
                              });
                            }}
                            style={{ flex: 2, minWidth: '180px', padding: '0.5rem', border: '1.5px solid #e2e8f0', borderRadius: '6px' }}
                            autoFocus
                          >
                            <option value="">Select token from VIO...</option>
                            {tokenList.map(token => {
                              const hasZeroBalance = !token.balance || token.balance <= 0;
                              return (
                                <option key={token.id} value={token.id} disabled={hasZeroBalance}>
                                  {token.name} ({token.symbol}) - Balance: {token.balance?.toLocaleString() || 0}{hasZeroBalance ? ' (Unavailable)' : ''}
                                </option>
                              );
                            })}
                          </select>
                        ) : (
                          <input
                            type="text"
                            placeholder="Token type (e.g., VIO Point)"
                            value={editingToken.tokenType}
                            onChange={(e) => setEditingToken({ ...editingToken, tokenType: e.target.value })}
                            style={{ flex: 2, minWidth: '150px', padding: '0.5rem', border: '1.5px solid #e2e8f0', borderRadius: '6px' }}
                            autoFocus
                          />
                        )}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                          <span style={{ color: '#64748b' }}>RM</span>
                          <input
                            type="number"
                            min="1"
                            placeholder="Spend"
                            value={editingToken.spendAmount}
                            onChange={(e) => setEditingToken({ ...editingToken, spendAmount: e.target.value })}
                            style={{ width: '80px', padding: '0.5rem', border: '1.5px solid #e2e8f0', borderRadius: '6px' }}
                          />
                        </div>
                        <span style={{ color: '#64748b', alignSelf: 'center' }}>→</span>
                        {(() => {
                          const selectedToken = tokenList?.find(t => t.id === editingToken.tokenId);
                          const maxBalance = selectedToken?.balance;
                          return (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                              <input
                                type="number"
                                min="1"
                                max={maxBalance}
                                placeholder="Earn"
                                value={editingToken.earnAmount}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  if (maxBalance && parseFloat(val) > maxBalance) {
                                    setEditingToken({ ...editingToken, earnAmount: maxBalance.toString() });
                                  } else {
                                    setEditingToken({ ...editingToken, earnAmount: val });
                                  }
                                }}
                                style={{ width: '80px', padding: '0.5rem', border: '1.5px solid #e2e8f0', borderRadius: '6px' }}
                                title={maxBalance ? `Max ${maxBalance.toLocaleString()} available` : ''}
                              />
                              <span style={{ color: '#64748b' }}>Token(s)</span>
                            </div>
                          );
                        })()}
                      </div>
                      {!tokenList?.length && (
                        <div style={{ fontSize: '0.75rem', color: '#94a3b8', fontStyle: 'italic' }}>
                          ⚠️ Could not load VIO tokens. Enter token type manually or check authentication.
                        </div>
                      )}
                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
                        <button className="btn-action secondary" onClick={handleCancelEditToken}>
                          Cancel
                        </button>
                        <button 
                          className="btn-action primary" 
                          onClick={handleSaveEditToken}
                          disabled={!editingToken.tokenType || !editingToken.spendAmount || parseFloat(editingToken.spendAmount) <= 0 || !editingToken.earnAmount || parseFloat(editingToken.earnAmount) <= 0}
                        >
                          Save Changes
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div key={rule.id} className="item-card">
                      <div className="item-info">
                        <div className="item-icon" style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }}>
                          🪙
                        </div>
                        <div className="item-details">
                          <h4>{rule.tokenType}</h4>
                          <span>Spend RM{rule.spendAmount} → Earn {rule.earnAmount} Token(s)</span>
                        </div>
                      </div>
                      <div className="item-actions">
                        <button 
                          className="btn-icon-sm"
                          onClick={() => handleStartEditToken(rule)}
                          title="Edit"
                          style={{ marginRight: '0.5rem' }}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                          </svg>
                        </button>
                        <button 
                          className="btn-icon-sm delete"
                          onClick={() => onDeleteTokenRule(rule.id)}
                          title="Remove"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M3 6h18"></path>
                            <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
                            <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
                          </svg>
                        </button>
                      </div>
                    </div>
                  )
                ))
              )}

              {isAddingToken && !editingTokenId && (formData.tokenRules || []).length === 0 ? (
                <div className="item-card" style={{ background: '#f8fafc', flexDirection: 'column', gap: '0.75rem', alignItems: 'stretch' }}>
                  <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
                    {tokenList && tokenList.length > 0 ? (
                      <select
                        value={newToken.tokenId}
                        onChange={(e) => {
                          const selected = tokenList.find(t => t.id === e.target.value);
                          setNewToken({ 
                            ...newToken, 
                            tokenId: e.target.value,
                            tokenType: selected ? `${selected.name} (${selected.symbol})` : ''
                          });
                        }}
                        style={{ flex: 2, minWidth: '180px', padding: '0.5rem', border: '1.5px solid #e2e8f0', borderRadius: '6px' }}
                        autoFocus
                      >
                        <option value="">Select token from VIO...</option>
                        {tokenList.map(token => {
                          const hasZeroBalance = !token.balance || token.balance <= 0;
                          return (
                            <option key={token.id} value={token.id} disabled={hasZeroBalance}>
                              {token.name} ({token.symbol}) - Balance: {token.balance?.toLocaleString() || 0}{hasZeroBalance ? ' (Unavailable)' : ''}
                            </option>
                          );
                        })}
                      </select>
                    ) : (
                      <input
                        type="text"
                        placeholder="Token type (e.g., VIO Point)"
                        value={newToken.tokenType}
                        onChange={(e) => setNewToken({ ...newToken, tokenType: e.target.value })}
                        style={{ flex: 2, minWidth: '150px', padding: '0.5rem', border: '1.5px solid #e2e8f0', borderRadius: '6px' }}
                        autoFocus
                      />
                    )}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                      <span style={{ color: '#64748b' }}>RM</span>
                      <input
                        type="number"
                        min="1"
                        placeholder="Spend"
                        value={newToken.spendAmount}
                        onChange={(e) => setNewToken({ ...newToken, spendAmount: e.target.value })}
                        style={{ width: '80px', padding: '0.5rem', border: '1.5px solid #e2e8f0', borderRadius: '6px' }}
                      />
                    </div>
                    <span style={{ color: '#64748b', alignSelf: 'center' }}>→</span>
                    {(() => {
                      const selectedToken = tokenList?.find(t => t.id === newToken.tokenId);
                      const maxBalance = selectedToken?.balance;
                      return (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                          <input
                            type="number"
                            min="1"
                            max={maxBalance}
                            placeholder="Earn"
                            value={newToken.earnAmount}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (maxBalance && parseFloat(val) > maxBalance) {
                                setNewToken({ ...newToken, earnAmount: maxBalance.toString() });
                              } else {
                                setNewToken({ ...newToken, earnAmount: val });
                              }
                            }}
                            style={{ width: '80px', padding: '0.5rem', border: '1.5px solid #e2e8f0', borderRadius: '6px' }}
                            title={maxBalance ? `Max ${maxBalance.toLocaleString()} available` : ''}
                          />
                          <span style={{ color: '#64748b' }}>Token(s)</span>
                        </div>
                      );
                    })()}
                  </div>
                  {!tokenList?.length && (
                    <div style={{ fontSize: '0.75rem', color: '#94a3b8', fontStyle: 'italic' }}>
                      ⚠️ Could not load VIO tokens. Enter token type manually or check authentication.
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
                    <button className="btn-action secondary" onClick={() => setIsAddingToken(false)}>
                      Cancel
                    </button>
                    <button 
                      className="btn-action primary" 
                      onClick={handleAddTokenRule}
                      disabled={!newToken.tokenType || !newToken.spendAmount || parseFloat(newToken.spendAmount) <= 0 || !newToken.earnAmount || parseFloat(newToken.earnAmount) <= 0}
                    >
                      Add Rule
                    </button>
                  </div>
                </div>
              ) : !editingTokenId && (formData.tokenRules || []).length === 0 ? (
                <button className="add-item-btn" onClick={() => {
                  setIsAddingToken(true);
                  setEditingTokenId(null);
                }}>
                  <span>+</span> Add Token Rule
                </button>
              ) : null}
            </div>
          </div>
        )}

        {message && (
          <div className={`status-message ${message.type}`}>
            {message.type === 'success' ? '✓' : '✕'} {message.text}
          </div>
        )}

        <div className="save-bar">
          <button 
            className="btn-save" 
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default RewardsTab;
